import React from 'react';
import { ArrowUpRight, ArrowDownLeft, Plus, Minus, QrCode, MessageSquare, Receipt } from 'lucide-react';
import { Badge } from '@vaiu/ui/badge';
import { Transaction } from '@/services/platformApi'
import { currencyService } from '@/services/currencyService';;
import { CurrencyDisplay } from '@vaiu/ui';
import { StatusBadge } from '@vaiu/ui';
import TokenRenderer from '@/components/shared/TokenRenderer';
import { isTokenTransaction } from '@/utils/tokenUtils';

interface TransactionItemProps {
  transaction: Transaction;
  onClick: () => void;
  isLast?: boolean;
}

const TransactionItem: React.FC<TransactionItemProps> = ({
  transaction,
  onClick,
  isLast = false
}) => {
  // Check if this is a token transaction and render with TokenRenderer for consistency
  if (isTokenTransaction(transaction)) {
    // Use the same token key logic as isTokenTransaction
    const tokenKey = transaction.key || transaction.tokenKey;
    
    return (
      <div className={`${isLast ? '' : 'border-b'}`}>
        <TokenRenderer 
          transaction={transaction} // Pass the full transaction object without transformation
          showTimestamp={true}
          onClick={onClick}
        />
      </div>
    );
  }

  // For non-token transactions (deposits, withdrawals, etc.), use the existing simple row format
  const getTransactionIcon = () => {
    const transactionType = transaction.type as string;
    const tokenType = transaction.tokenType as string;
    
    // Handle balance operations
    if (tokenType === 'balance_up') {
      return <Plus className="w-5 h-5 text-green-600" />;
    }
    if (tokenType === 'balance_down') {
      return <Minus className="w-5 h-5 text-red-600" />;
    }
    
    switch (transactionType) {
      case 'send':
        return <ArrowUpRight className="w-5 h-5 text-red-600" />;
      case 'receive':
        return <ArrowDownLeft className="w-5 h-5 text-green-600" />;
      case 'debit':
        return <ArrowUpRight className="w-5 h-5 text-red-600" />;
      case 'credit':
        return <ArrowDownLeft className="w-5 h-5 text-green-600" />;
      case 'deposit':
        return <Plus className="w-5 h-5 text-green-600" />;
      case 'withdraw':
        return <Minus className="w-5 h-5 text-red-600" />;
      case 'redeem':
        return <QrCode className="w-5 h-5 text-blue-600" />;
      default:
        return <ArrowUpRight className="w-5 h-5" />;
    }
  };

  const getTransactionTitle = () => {
    const transactionType = transaction.type as string;
    const tokenType = transaction.tokenType as string;
    
    // Handle balance_up (topup) transactions from platform
    if (tokenType === 'balance_up') {
      const source = (transaction as any).source || '';
      if (source === 'paypal') {
        return 'Balance top-up via PayPal';
      } else if (source === 'stripe') {
        return 'Balance top-up via Stripe';
      } else if (source === 'ach' || source === 'bank') {
        return 'Balance top-up via Bank';
      }
      return 'Balance top-up';
    }
    
    // Handle balance_down (transfer out) transactions
    if (tokenType === 'balance_down') {
      const source = (transaction as any).source || '';
      if (source === 'paypal') {
        return 'Transfer out via PayPal';
      } else if (source === 'stripe') {
        return 'Transfer out via Stripe';
      } else if (source === 'ach' || source === 'bank') {
        return 'Transfer out via Bank';
      }
      return 'Transfer out';
    }
    
    // First check if there's a specific message in the transaction that suggests a better title
    const description = transaction.description || transaction.message || (transaction as any).data?.message || '';
    
    // Handle topups/deposits with better titles
    if (transactionType === 'deposit' || transactionType === 'credit' || tokenType === 'topup') {
      if (description.toLowerCase().includes('stripe')) {
        return 'Payment received';
      }
      if (description.toLowerCase().includes('paypal')) {
        return 'Payment received';
      }
      if (description.toLowerCase().includes('bank')) {
        return 'Payment received';
      }
      if (description.toLowerCase().includes('top-up') || description.toLowerCase().includes('topup')) {
        return 'Payment received';
      }
    }
    
    switch (transactionType) {
      case 'send':
        return transaction.counterparty ? `Sent to ${transaction.counterparty.name}` : 'Payment sent';
      case 'receive':
        return transaction.counterparty ? `Received from ${transaction.counterparty.name}` : 'Payment received';
      case 'debit':
        return 'Payment sent';
      case 'credit':
        return 'Payment received';
      case 'deposit':
        return 'Payment received'; // Better than generic "Deposit"
      case 'withdraw':
        return 'Withdrawal';
      case 'redeem':
        return 'Token redeemed';
      default:
        return 'Transaction';
    }
  };

  const getAmountDisplay = () => {
    const isNegative = transaction.type === 'send' || transaction.type === 'withdraw' || transaction.type === 'debit';
    // Handle amount as string or number (platform returns topup amounts as strings)
    const amount = typeof transaction.amount === 'string' 
      ? parseFloat(transaction.amount) 
      : transaction.amount;
    return (
      <CurrencyDisplay amount={amount}
        currency={transaction.currency}
        showSign={true}
        variant={isNegative ? 'destructive' : 'success'}
        className={isNegative ? 'text-red-600' : 'text-green-600'} currencyService={currencyService}
      />
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return "Unknown date";
    }
    
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }
    
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  return (
    <div
      onClick={onClick}
      className={`px-1 py-2 hover:bg-muted/50 cursor-pointer transition-colors ${
        isLast ? '' : 'border-b'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-muted rounded-full">
            {getTransactionIcon()}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="font-medium">{getTransactionTitle()}</h3>
              {transaction.conversationId && (
                <MessageSquare className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
            
            <div className="flex items-center space-x-2 mt-1">
              <p className="text-sm text-muted-foreground">
                {transaction.description || transaction.message || (transaction as any).data?.message || 'No description'}
              </p>
            </div>

            {/* Invoice Payment Indicator */}
            {transaction.type === 'cash' && (
              (transaction as any).paidInvoice || 
              (transaction as any).invoiceKey || 
              (transaction as any).invoice_key ||
              (transaction as any).purpose?.includes('invoice') ||
              (transaction as any).metadata?.type === 'invoice_payment' ||
              (transaction as any).metadata?.paidInvoice
            ) && (
              <div className="flex items-center space-x-1 mt-1">
                <Receipt className="w-3 h-3 text-orange-600" />
                <span className="text-xs text-orange-600">Invoice payment</span>
              </div>
            )}
            
            <p className="text-xs text-muted-foreground mt-1">
              {formatDate(transaction.createdAt || transaction.date || transaction.created_at || transaction.dateCreated)}
            </p>
          </div>
        </div>
        
        <div className="text-right space-y-1">
          <div className="font-medium">
            {getAmountDisplay()}
          </div>
          <StatusBadge 
            status={(() => {
              // Balance operations (topups and transfers) that appear in transaction list are always completed
              // (they only appear after successful processing)
              const tokenType = transaction.tokenType as string;
              if (transaction.type === 'deposit' || 
                  transaction.type === 'credit' || 
                  tokenType === 'balance_up' || 
                  tokenType === 'balance_down' ||
                  tokenType === 'topup') {
                return 'completed';
              }
              // Normalize status to lowercase for StatusBadge component
              const status = transaction.status as string;
              return status?.toLowerCase() || status;
            })()}
            size="sm"
          />
        </div>
      </div>
    </div>
  );
};

export default TransactionItem; 